export const articles = {
  "cybersecurity-macchine": {
    heroImage: "stock:cyber",
    it: {
      overline: "Approfondimenti · Cybersecurity",
      title: "Cybersecurity delle macchine: cosa cambia con il Regolamento (UE) 2023/1230",
      lead: "Le macchine sono sempre più connesse, e la sicurezza informatica diventa requisito di legge: dal 20 gennaio 2027 il nuovo Regolamento Macchine impone ai costruttori di proteggere software, dati e funzioni di sicurezza da manomissioni e accessi non autorizzati.",
      sections: [
        {
          heading: "Perché la cybersecurity entra nel Regolamento Macchine",
          paragraph:
            "Digitalizzazione e interconnessione hanno reso gli impianti più efficienti, ma anche più esposti: un attacco informatico a una macchina non compromette solo i dati, può alterare le funzioni di sicurezza e generare situazioni di pericolo reale per le persone. Per questo il legislatore europeo ha smesso di trattare sicurezza funzionale (safety) e sicurezza informatica (security) come mondi separati: oggi sono due facce della stessa conformità.",
        },
        {
          heading: "Cosa chiede il nuovo Regolamento",
          paragraph:
            "Il Regolamento (UE) 2023/1230 introduce requisiti essenziali di sicurezza dedicati al digitale. In sintesi:",
          list: [
            "Protezione dalla corruzione: la connessione o la comunicazione con altri dispositivi non deve generare situazioni pericolose (Allegato III, 1.1.9)",
            "Software e dati di sicurezza protetti da manipolazioni intenzionali e accessi non autorizzati",
            "Possibilità di registrare e conservare i dati relativi alla sicurezza",
            "Aggiornabilità del software per tutto il ciclo di vita, per correggere le vulnerabilità",
            "Documentazione tecnica e istruzioni anche in formato digitale",
          ],
        },
        {
          heading: "Il quadro completo: Regolamento, CRA e NIS2",
          paragraph:
            "Il Regolamento Macchine non agisce da solo. Il Cyber Resilience Act (UE) 2024/2847 fissa obblighi di cybersicurezza per i prodotti con elementi digitali immessi sul mercato europeo — sviluppo sicuro, gestione delle vulnerabilità e supporto per almeno cinque anni — con effetti pieni da dicembre 2027. La direttiva NIS2 (UE) 2022/2555, già in vigore, riguarda invece le aziende: gestione del rischio, misure organizzative e notifica tempestiva degli incidenti. Costruttori e utilizzatori sono entrambi dentro questo perimetro.",
        },
        {
          heading: "Safety + Security: un unico approccio",
          paragraph:
            "Una barriera fotoelettrica o un arresto di emergenza manipolabili da remoto non sono più un presidio di sicurezza. L'analisi dei rischi deve quindi diventare integrata: accanto ai pericoli meccanici, elettrici ed ergonomici valutati secondo EN ISO 12100, EN ISO 13849 e IEC 62061, il fascicolo tecnico deve considerare anche le minacce informatiche che possono compromettere le funzioni di sicurezza.",
        },
        {
          heading: "Lo standard di riferimento: IEC 62443",
          paragraph:
            "Per dimostrare la conformità, la famiglia di norme IEC 62443 è oggi il quadro riconosciuto a livello internazionale: definisce livelli di sicurezza (Security Level 0-4), il modello «defense in depth» con segmentazione della rete in zone e condotti, e i requisiti per componenti e sistemi. È inoltre in sviluppo la norma EN 50742, specifica per la protezione dalla corruzione richiesta dal Regolamento. A livello organizzativo, un sistema di gestione della sicurezza delle informazioni (ISO/IEC 27001) completa il quadro.",
        },
        {
          heading: "Cosa fare concretamente: checklist per i costruttori",
          list: [
            "Integrare la valutazione dei rischi cyber nell'analisi dei rischi della macchina e nel fascicolo tecnico",
            "Progettare «secure by design» e «secure by default»: la sicurezza attiva fin dal primo avvio",
            "Definire un processo di gestione delle vulnerabilità e degli aggiornamenti per l'intero ciclo di vita",
            "Segmentare le reti e separare produzione e uffici, con controllo degli accessi e autenticazione",
            "Verificare la conformità dei componenti di automazione (PLC, azionamenti, HMI) alla IEC 62443",
            "Aggiornare manuali e documentazione con le istruzioni per l'uso sicuro della macchina connessa",
          ],
        },
      ],
      cta: { label: "Valuta la conformità cyber delle tue macchine", to: "/contatti" },
    },
    en: {
      overline: "Insights · Cybersecurity",
      title: "Machinery cybersecurity: what changes with Regulation (EU) 2023/1230",
      lead: "Machines are increasingly connected, and cybersecurity is becoming a legal requirement: from 20 January 2027 the new Machinery Regulation requires manufacturers to protect software, data and safety functions from tampering and unauthorised access.",
      sections: [
        {
          heading: "Why cybersecurity enters the Machinery Regulation",
          paragraph:
            "Digitalisation and interconnectivity have made plants more efficient, but also more exposed: a cyberattack on a machine does not only compromise data — it can alter safety functions and create real danger for people. This is why the European legislator has stopped treating functional safety and cybersecurity as separate worlds: today they are two sides of the same compliance.",
        },
        {
          heading: "What the new Regulation requires",
          paragraph:
            "Regulation (EU) 2023/1230 introduces essential health and safety requirements dedicated to the digital domain. In short:",
          list: [
            "Protection against corruption: connection or communication with other devices must not create hazardous situations (Annex III, 1.1.9)",
            "Software and safety-related data protected from intentional manipulation and unauthorised access",
            "Ability to record and retain safety-related data",
            "Software updateability throughout the lifecycle, to fix vulnerabilities",
            "Technical documentation and instructions also in digital format",
          ],
        },
        {
          heading: "The full picture: Regulation, CRA and NIS2",
          paragraph:
            "The Machinery Regulation does not act alone. The Cyber Resilience Act (EU) 2024/2847 sets cybersecurity obligations for products with digital elements placed on the European market — secure development, vulnerability handling and at least five years of support — with full effect from December 2027. The NIS2 Directive (EU) 2022/2555, already in force, targets companies instead: risk management, organisational measures and prompt incident reporting. Manufacturers and users are both within this perimeter.",
        },
        {
          heading: "Safety + Security: one single approach",
          paragraph:
            "A light curtain or an emergency stop that can be manipulated remotely is no longer a safety device. Risk assessment must therefore become integrated: alongside the mechanical, electrical and ergonomic hazards assessed under EN ISO 12100, EN ISO 13849 and IEC 62061, the technical file must also consider cyber threats that could compromise the safety functions.",
        },
        {
          heading: "The reference standard: IEC 62443",
          paragraph:
            "To demonstrate compliance, the IEC 62443 family of standards is today the internationally recognised framework: it defines Security Levels (0-4), the defence-in-depth model with network segmentation into zones and conduits, and requirements for components and systems. The EN 50742 standard, specific to the protection against corruption required by the Regulation, is also under development. At organisational level, an information security management system (ISO/IEC 27001) completes the picture.",
        },
        {
          heading: "Concrete steps: a checklist for manufacturers",
          list: [
            "Integrate cyber risk assessment into the machine risk analysis and the technical file",
            "Design secure by design and secure by default: security active from the first start-up",
            "Define a vulnerability and update management process for the entire lifecycle",
            "Segment networks and separate production from offices, with access control and authentication",
            "Verify IEC 62443 compliance of automation components (PLC, drives, HMI)",
            "Update manuals and documentation with instructions for the safe use of the connected machine",
          ],
        },
      ],
      cta: { label: "Assess the cyber compliance of your machines", to: "/contatti" },
    },
  },
  "manuale-uso": {
    heroImage: "/articles/manuale-uso.png",
    it: {
      overline: "Approfondimenti · Documentazione",
      title: "Il manuale d'uso: istruzioni che proteggono (e fanno vendere)",
      lead: "Il manuale d'uso e manutenzione non è un accessorio: è parte integrante della macchina, obbligatorio per legge e decisivo per la sicurezza di chi la usa. Ecco cosa deve contenere e perché conviene farlo bene.",
      sections: [
        {
          heading: "Parte integrante del prodotto",
          paragraph:
            "Senza manuale, nessuna macchina può essere immessa sul mercato europeo. Lo stabilisce la normativa macchine da sempre, e lo ribadiamo nel nostro libro «La Nuova Direttiva Macchine»: la documentazione è a tutti gli effetti un componente di sicurezza. Deve accompagnare la macchina per tutta la sua vita, dal trasporto alla dismissione.",
        },
        {
          heading: "Cosa deve contenere",
          paragraph:
            "I contenuti minimi sono definiti dalla normativa (Allegato I). In sintesi, un manuale conforme include:",
          list: [
            "Ragione sociale e indirizzo del fabbricante e, se esiste, del mandatario",
            "Descrizione della macchina e uso previsto, incluso l'uso scorretto ragionevolmente prevedibile",
            "Istruzioni per trasporto, movimentazione, installazione, messa in servizio, uso, manutenzione e smontaggio",
            "Avvertenze sui rischi residui che permangono nonostante le misure di protezione",
            "Dati sulle emissioni: rumore, vibrazioni, radiazioni",
            "Per le quasi-macchine: le istruzioni di assemblaggio",
          ],
        },
        {
          heading: "La lingua conta",
          paragraph:
            "Le istruzioni devono essere redatte in una lingua ufficiale dell'UE dal fabbricante (istruzioni originali) e tradotte nella lingua del Paese in cui la macchina è usata, con la dicitura «Traduzione delle istruzioni originali». Una traduzione approssimativa non è solo un rischio giuridico: è un rischio per chi opera sulla macchina.",
        },
        {
          heading: "Gli errori più comuni",
          list: [
            "Manuali generici copiati da un modello all'altro, senza adattamenti",
            "Rischi residui non descritti o liquidati in una riga",
            "Traduzioni automatiche non revisionate da madrelingua tecnici",
            "Istruzioni di manutenzione assenti o incomplete",
            "Manuale non aggiornato dopo modifiche o retrofit della macchina",
          ],
        },
        {
          heading: "Con il Regolamento (UE) 2023/1230",
          paragraph:
            "Dal 20 gennaio 2027 le istruzioni potranno essere fornite in formato digitale, purché scaricabili, stampabili e conservabili per tutta la vita della macchina, con la versione cartacea gratuita su richiesta del cliente. Un'opportunità concreta: con un QR code sulla macchina — la logica alla base del nostro prodotto CertificoQR — il manuale è sempre accessibile, aggiornato e nella lingua giusta.",
        },
      ],
      cta: { label: "Parla con i nostri tecnici", to: "/servizi/manualistica" },
    },
    en: {
      overline: "Insights · Documentation",
      title: "The user manual: instructions that protect (and help sell)",
      lead: "The use and maintenance manual is not an accessory: it is an integral part of the machine, required by law and decisive for the safety of its users. Here is what it must contain and why it pays to do it well.",
      sections: [
        {
          heading: "An integral part of the product",
          paragraph:
            "Without a manual, no machine can be placed on the European market. Machinery legislation has always stated this, and we repeat it in our book «La Nuova Direttiva Macchine»: documentation is a safety component in every respect. It must accompany the machine throughout its life, from transport to disposal.",
        },
        {
          heading: "What it must contain",
          paragraph:
            "The minimum contents are defined by the legislation (Annex I). In short, a compliant manual includes:",
          list: [
            "Name and address of the manufacturer and, where applicable, of the authorised representative",
            "Description of the machine and its intended use, including reasonably foreseeable misuse",
            "Instructions for transport, handling, installation, commissioning, use, maintenance and disassembly",
            "Warnings on residual risks that remain despite protective measures",
            "Emissions data: noise, vibrations, radiation",
            "For partly completed machinery: the assembly instructions",
          ],
        },
        {
          heading: "Language matters",
          paragraph:
            "Instructions must be written by the manufacturer in an official EU language (original instructions) and translated into the language of the country where the machine is used, marked as «Translation of the original instructions». A sloppy translation is not only a legal risk: it is a risk for the people operating the machine.",
        },
        {
          heading: "The most common mistakes",
          list: [
            "Generic manuals copied from one model to another, without adaptation",
            "Residual risks not described or dismissed in a single line",
            "Machine translations not reviewed by native technical translators",
            "Missing or incomplete maintenance instructions",
            "Manual not updated after machine modifications or retrofits",
          ],
        },
        {
          heading: "With Regulation (EU) 2023/1230",
          paragraph:
            "From 20 January 2027 instructions may be supplied in digital format, provided they are downloadable, printable and kept accessible for the machine's entire life, with a free paper version on customer request. A concrete opportunity: with a QR code on the machine — the logic behind our CertificoQR product — the manual is always accessible, up to date and in the right language.",
        },
      ],
      cta: { label: "Talk to our technicians", to: "/servizi/manualistica" },
    },
  },
  "fascicolo-tecnico": {
    heroImage: "/articles/fascicolo-tecnico.png",
    it: {
      overline: "Approfondimenti · Sicurezza",
      title: "Il fascicolo tecnico: la memoria della macchina",
      lead: "Disegni, calcoli, prove, valutazione dei rischi: il fascicolo tecnico è la prova documentale che la macchina è nata conforme. Il fabbricante deve redigerlo e conservarlo: ecco cosa contiene davvero e gli errori da evitare.",
      sections: [
        {
          heading: "Cos'è e chi lo redige",
          paragraph:
            "Il fascicolo tecnico è l'insieme dei documenti che dimostrano la conformità della macchina ai requisiti essenziali di sicurezza. Lo redige il fabbricante (o il suo mandatario) prima dell'immissione sul mercato; per le quasi-macchine la normativa parla di «documentazione tecnica pertinente», con contenuti analoghi.",
        },
        {
          heading: "Cosa contiene",
          paragraph:
            "Il contenuto è definito dalla normativa (Allegato VII). In sintesi:",
          list: [
            "Disegno generale della macchina e schemi dei circuiti di comando",
            "Descrizioni e spiegazioni necessarie a comprendere funzionamento e uso",
            "La documentazione completa della valutazione dei rischi: pericoli individuati, stime, misure adottate",
            "L'elenco delle norme armonizzate e delle specifiche tecniche applicate",
            "Relazioni su calcoli, prove e controlli effettuati (incluse le verifiche sui circuiti di sicurezza, PL/SIL)",
            "Copia della dichiarazione di conformità; per le quasi-macchine: dichiarazione di incorporazione e istruzioni di assemblaggio",
          ],
        },
        {
          heading: "Il legame con la Dichiarazione di conformità",
          paragraph:
            "Un dettaglio spesso trascurato: la Dichiarazione di conformità deve indicare nome e indirizzo della persona autorizzata a costituire il fascicolo tecnico, stabilita nell'Unione. Non è un'anagrafica formale: è il punto di contatto verso cui convergono le responsabilità documentali.",
        },
        {
          heading: "Non è per il cliente",
          paragraph:
            "Il fascicolo contiene informazioni riservate — know-how, calcoli, scelte progettuali — e non va consegnato integralmente all'utilizzatore. Va invece reso disponibile alle autorità di sorveglianza del mercato in caso di richiesta. Al cliente spettano manuale, dichiarazione e documentazione d'uso: è una distinzione che conta.",
        },
        {
          heading: "Gli errori che costano cari",
          list: [
            "Fascicolo ricostruito a posteriori, quando la macchina è già venduta",
            "Valutazione dei rischi mancante, generica o non firmata",
            "Prove e verifiche eseguite ma non documentate",
            "Nessun responsabile del fascicolo nominato nella dichiarazione",
            "Documentazione non aggiornata dopo modifiche alla macchina",
          ],
        },
        {
          heading: "Con il Regolamento (UE) 2023/1230",
          paragraph:
            "Il Regolamento conferma l'impianto e lo porta nel digitale: il fascicolo può essere costituito e conservato in formato elettronico, deve restare disponibile alle autorità per almeno dieci anni e può essere reso accessibile anche a distanza. Un archivio digitale strutturato — come quello che CertificoQR collega a ogni macchina — trasforma un obbligo in un vantaggio operativo.",
        },
      ],
      cta: { label: "Verifica il tuo fascicolo tecnico", to: "/contatti" },
    },
    en: {
      overline: "Insights · Safety",
      title: "The technical file: the machine's memory",
      lead: "Drawings, calculations, tests, risk assessment: the technical file is the documentary proof that the machine was born compliant. The manufacturer must draw it up and keep it: here is what it really contains and the mistakes to avoid.",
      sections: [
        {
          heading: "What it is and who draws it up",
          paragraph:
            "The technical file is the set of documents demonstrating the machine's conformity with the essential health and safety requirements. It is drawn up by the manufacturer (or the authorised representative) before the machine is placed on the market; for partly completed machinery the legislation speaks of «relevant technical documentation», with similar contents.",
        },
        {
          heading: "What it contains",
          paragraph:
            "The contents are defined by the legislation (Annex VII). In short:",
          list: [
            "General drawing of the machine and control circuit diagrams",
            "Descriptions and explanations needed to understand its operation and use",
            "The complete risk assessment documentation: hazards identified, estimates, measures adopted",
            "The list of harmonised standards and technical specifications applied",
            "Reports on the calculations, tests and checks carried out (including safety circuit verification, PL/SIL)",
            "Copy of the declaration of conformity; for partly completed machinery: declaration of incorporation and assembly instructions",
          ],
        },
        {
          heading: "The link with the Declaration of Conformity",
          paragraph:
            "An often-overlooked detail: the Declaration of Conformity must state the name and address of the person authorised to compile the technical file, established in the Union. This is not a formality: it is the contact point to which documentary responsibilities converge.",
        },
        {
          heading: "It is not for the customer",
          paragraph:
            "The file contains confidential information — know-how, calculations, design choices — and must not be handed over in full to the user. It must instead be made available to market surveillance authorities on request. The customer receives the manual, the declaration and the user documentation: a distinction that matters.",
        },
        {
          heading: "The mistakes that cost dearly",
          list: [
            "File reconstructed afterwards, when the machine is already sold",
            "Risk assessment missing, generic or unsigned",
            "Tests and checks performed but not documented",
            "No file-responsible person named in the declaration",
            "Documentation not updated after machine modifications",
          ],
        },
        {
          heading: "With Regulation (EU) 2023/1230",
          paragraph:
            "The Regulation confirms the framework and brings it into the digital age: the file may be compiled and kept in electronic format, must remain available to authorities for at least ten years and can be made accessible remotely. A structured digital archive — like the one CertificoQR links to every machine — turns an obligation into an operational advantage.",
        },
      ],
      cta: { label: "Check your technical file", to: "/contatti" },
    },
  },
  "marcatura-ce": {
    heroImage: "/articles/marcatura-ce.png",
    it: {
      overline: "Approfondimenti · Conformità",
      title: "Marcatura CE delle macchine: regole, percorso e responsabilità",
      lead: "Apporre il marchio CE è l'ultimo passo di un percorso, non un adesivo da applicare: con quel marchio il fabbricante dichiara — assumendosene la responsabilità — che la macchina è conforme a tutti i requisiti applicabili.",
      sections: [
        {
          heading: "Cosa significa davvero il marchio CE",
          paragraph:
            "Il marchio CE è l'unico marchio che attesta la conformità a tutte le direttive e i regolamenti europei applicabili al prodotto. Non è un marchio di qualità né una certificazione volontaria: è una dichiarazione di responsabilità del fabbricante, e apre le porte alla libera circolazione della macchina in tutto lo Spazio Economico Europeo.",
        },
        {
          heading: "Chi lo appone e quando",
          paragraph:
            "La marcatura spetta al fabbricante o al suo mandatario stabilito nell'Unione, e deve avvenire prima dell'immissione sul mercato e della messa in servizio della macchina. Nessun marchio senza percorso: chi appone il CE senza aver completato la valutazione di conformità si assume una responsabilità civile e penale.",
        },
        {
          heading: "Le regole formali",
          list: [
            "Il marchio deve essere visibile, leggibile e indelebile",
            "Le proporzioni vanno rispettate anche in caso di ingrandimento o riduzione",
            "È apposto sulla macchina, vicino ai dati di targa (in alcuni casi anche sull'imballo)",
            "Se un organismo notificato è intervenuto nel controllo della produzione, il suo numero identificativo segue il marchio",
            "Il marchio CE è l'unico marchio di conformità: altri marchi non devono creare confusione con esso",
          ],
        },
        {
          heading: "Il percorso prima del marchio",
          paragraph:
            "La marcatura è l'ultimo anello di una catena documentata:",
          list: [
            "Valutazione dei rischi secondo EN ISO 12100",
            "Applicazione dei requisiti essenziali di sicurezza e di tutela della salute",
            "Progettazione e verifica dei circuiti di comando di sicurezza (PL secondo EN ISO 13849-1 o SIL secondo IEC 62061)",
            "Prove strumentali: rumore, vibrazioni, EMC dove applicabili",
            "Costituzione del fascicolo tecnico",
            "Redazione della Dichiarazione di conformità",
            "Solo a questo punto: marcatura CE e consegna del manuale nella lingua corretta",
          ],
        },
        {
          heading: "Sorveglianza del mercato",
          paragraph:
            "Le autorità degli Stati membri possono controllare le macchine in circolazione, chiedere il fascicolo tecnico e disporre ritiro o richiamo in caso di non conformità: è la cosiddetta clausola di salvaguardia. Una marcatura CE leggera espone l'azienda a fermi commerciali, sanzioni e danni d'immagine.",
        },
        {
          heading: "Con il Regolamento (UE) 2023/1230",
          paragraph:
            "Dal 20 gennaio 2027 il marchio resta, ma cambia il contesto: il riferimento diventa il Regolamento (non più la Direttiva 2006/42/CE), entrano requisiti digitali e di cybersecurity, e la Dichiarazione di conformità potrà essere digitale e accessibile online. Prepararsi ora significa arrivare al 2027 senza interruzioni commerciali.",
        },
      ],
      cta: { label: "Affida il percorso CE a chi lo fa da 25 anni", to: "/contatti" },
    },
    en: {
      overline: "Insights · Compliance",
      title: "CE marking of machinery: rules, process and responsibilities",
      lead: "Affixing the CE mark is the last step of a process, not a sticker to apply: with that mark the manufacturer declares — taking responsibility for it — that the machine complies with all applicable requirements.",
      sections: [
        {
          heading: "What the CE mark really means",
          paragraph:
            "The CE mark is the only mark attesting conformity with all European directives and regulations applicable to the product. It is not a quality mark or a voluntary certification: it is a declaration of responsibility by the manufacturer, and it opens the door to free circulation of the machine throughout the European Economic Area.",
        },
        {
          heading: "Who affixes it and when",
          paragraph:
            "Marking is the responsibility of the manufacturer or their authorised representative established in the Union, and must take place before the machine is placed on the market and put into service. No mark without a process: anyone affixing the CE without completing the conformity assessment takes on civil and criminal liability.",
        },
        {
          heading: "The formal rules",
          list: [
            "The mark must be visible, legible and indelible",
            "Proportions must be respected even when enlarged or reduced",
            "It is affixed to the machine, near the nameplate data (in some cases also on the packaging)",
            "If a notified body took part in production control, its identification number follows the mark",
            "The CE mark is the only conformity mark: other marks must not create confusion with it",
          ],
        },
        {
          heading: "The process before the mark",
          paragraph:
            "Marking is the last link in a documented chain:",
          list: [
            "Risk assessment according to EN ISO 12100",
            "Application of the essential health and safety requirements",
            "Design and verification of safety-related control circuits (PL per EN ISO 13849-1 or SIL per IEC 62061)",
            "Instrumental tests: noise, vibrations, EMC where applicable",
            "Compilation of the technical file",
            "Drafting of the Declaration of Conformity",
            "Only then: CE marking and delivery of the manual in the correct language",
          ],
        },
        {
          heading: "Market surveillance",
          paragraph:
            "Member State authorities can inspect machines on the market, request the technical file and order withdrawal or recall in case of non-conformity: the so-called safeguard clause. Careless CE marking exposes the company to commercial stoppages, penalties and reputational damage.",
        },
        {
          heading: "With Regulation (EU) 2023/1230",
          paragraph:
            "From 20 January 2027 the mark remains, but the context changes: the reference becomes the Regulation (no longer Directive 2006/42/EC), digital and cybersecurity requirements come into force, and the Declaration of Conformity may be digital and accessible online. Preparing now means reaching 2027 without commercial interruptions.",
        },
      ],
      cta: { label: "Entrust the CE process to those who have done it for 25 years", to: "/contatti" },
    },
  },
  "valutazione-rischi": {
    heroImage: "/articles/valutazione-rischi.png",
    it: {
      overline: "Approfondimenti · Sicurezza",
      title: "La valutazione dei rischi: il cuore della conformità della macchina",
      lead: "Ogni macchina conforme nasce da una valutazione dei rischi seria e documentata: è il primo obbligo del fabbricante e il fondamento di tutte le scelte di sicurezza successive. Ecco come funziona il processo, passo per passo.",
      sections: [
        {
          heading: "Un obbligo, non una scelta",
          paragraph:
            "La normativa macchine lo stabilisce in apertura: il fabbricante, o il suo mandatario, deve garantire che sia effettuata una valutazione dei rischi per stabilire i requisiti di sicurezza e di tutela della salute che riguardano la macchina. E la macchina deve essere progettata e costruita tenendo conto dei risultati di quella valutazione. Non è un adempimento formale: è la base giuridica e tecnica di tutto il resto.",
        },
        {
          heading: "Il processo iterativo",
          paragraph:
            "La valutazione dei rischi è un ciclo che si ripete finché il rischio non è adeguatamente ridotto:",
          list: [
            "Stabilire i limiti della macchina: uso previsto e uso scorretto ragionevolmente prevedibile",
            "Individuare i pericoli che la macchina può generare e le situazioni pericolose che ne derivano",
            "Stimare i rischi: gravità della possibile lesione combinata con la probabilità che si verifichi",
            "Valutare i rischi per decidere se è necessaria una riduzione",
            "Eliminare i pericoli o ridurre i rischi applicando le misure di protezione nell'ordine corretto",
          ],
        },
        {
          heading: "La norma di riferimento: EN ISO 12100",
          paragraph:
            "Il quadro metodologico è la EN ISO 12100, che definisce terminologia, strategia e iterazione del processo. Accanto ad essa operano le norme di tipo C per le singole famiglie di macchine e, per le funzioni di sicurezza, la EN ISO 13849-1 e la IEC 62061, che trattiamo nell'approfondimento dedicato alla sicurezza funzionale.",
        },
        {
          heading: "Le tecniche di valutazione",
          paragraph:
            "Non esiste un unico metodo: la scelta dipende dalla macchina e dalla fase di progetto. Tra le tecniche più utilizzate — che trattiamo in dettaglio nel nostro libro sulla Direttiva Macchine — ci sono le check-list strutturate, la PHA (Preliminary Hazard Analysis), la FMEA per i guasti dei componenti, l'FTA per l'analisi ad albero dei guasti e gli indici quantitativi che combinano probabilità, gravità e possibilità di evitamento del danno.",
        },
        {
          heading: "L'ordine delle misure conta",
          list: [
            "Primo: progettazione intrinsecamente sicura — eliminare il pericolo alla fonte",
            "Secondo: misure di protezione tecniche — ripari, interblocchi, dispositivi sensibili",
            "Terzo: informazioni per l'utente — manuale, avvertenze sui rischi residui, segnaletica",
          ],
        },
        {
          heading: "Documentare per difendersi",
          paragraph:
            "La documentazione della valutazione dei rischi è parte integrante del fascicolo tecnico: in caso di infortunio o di controllo delle autorità, è la prova che il fabbricante ha fatto tutto ciò che era ragionevolmente richiesto. Una valutazione generica o ricostruita a posteriori è il primo elemento che un perito va a cercare — e il primo che conviene curare.",
        },
      ],
      cta: { label: "Richiedi una valutazione dei rischi", to: "/risk-analysis" },
    },
    en: {
      overline: "Insights · Safety",
      title: "Risk assessment: the heart of machine compliance",
      lead: "Every compliant machine starts from a serious, documented risk assessment: it is the manufacturer's first obligation and the foundation of every subsequent safety choice. Here is how the process works, step by step.",
      sections: [
        {
          heading: "An obligation, not a choice",
          paragraph:
            "Machinery legislation states it upfront: the manufacturer, or their authorised representative, must ensure that a risk assessment is carried out to establish the health and safety requirements applying to the machine. And the machine must be designed and constructed taking the results of that assessment into account. This is not a formality: it is the legal and technical basis of everything else.",
        },
        {
          heading: "The iterative process",
          paragraph:
            "Risk assessment is a cycle that repeats until the risk is adequately reduced:",
          list: [
            "Determine the limits of the machine: intended use and reasonably foreseeable misuse",
            "Identify the hazards the machine can generate and the resulting hazardous situations",
            "Estimate the risks: severity of possible harm combined with the probability of occurrence",
            "Evaluate the risks to decide whether reduction is needed",
            "Eliminate hazards or reduce risks by applying protective measures in the correct order",
          ],
        },
        {
          heading: "The reference standard: EN ISO 12100",
          paragraph:
            "The methodological framework is EN ISO 12100, which defines the terminology, strategy and iteration of the process. Alongside it operate the type-C standards for individual machine families and, for safety functions, EN ISO 13849-1 and IEC 62061, covered in our dedicated insight on functional safety.",
        },
        {
          heading: "Assessment techniques",
          paragraph:
            "There is no single method: the choice depends on the machine and the design phase. Among the most widely used techniques — covered in detail in our book on the Machinery Directive — are structured checklists, PHA (Preliminary Hazard Analysis), FMEA for component failures, FTA for fault-tree analysis and quantitative indices combining probability, severity and the possibility of avoiding harm.",
        },
        {
          heading: "The order of measures matters",
          list: [
            "First: inherently safe design — eliminate the hazard at the source",
            "Second: technical protective measures — guards, interlocks, sensitive protective devices",
            "Third: information for use — manual, residual risk warnings, signage",
          ],
        },
        {
          heading: "Document to defend yourself",
          paragraph:
            "Risk assessment documentation is an integral part of the technical file: in the event of an accident or an authority inspection, it is the proof that the manufacturer did everything reasonably required. A generic or retrospectively reconstructed assessment is the first thing an expert looks for — and the first thing worth getting right.",
        },
      ],
      cta: { label: "Request a risk assessment", to: "/risk-analysis" },
    },
  },
  "sicurezza-funzionale": {
    heroImage: "/articles/sicurezza-funzionale.png",
    it: {
      overline: "Approfondimenti · Sicurezza",
      title: "Sicurezza funzionale: PL e SIL spiegati ai costruttori",
      lead: "Arresto di emergenza, ripari mobili interbloccati, barriere fotoelettriche: le funzioni di sicurezza devono essere affidabili. Le norme EN ISO 13849-1 (PL) e IEC 62061 (SIL) dicono esattamente quanto — e come dimostrarlo.",
      sections: [
        {
          heading: "Cos'è la sicurezza funzionale",
          paragraph:
            "È la parte di sicurezza della macchina che dipende dal corretto funzionamento del sistema di comando: quando l'operatore apre un riparo o preme il fungo di emergenza, la funzione di sicurezza deve intervenire con un livello di affidabilità adeguato al rischio. Non basta che «funzioni»: deve funzionare anche quando qualcosa si guasta.",
        },
        {
          heading: "EN ISO 13849-1: i Performance Level",
          paragraph:
            "Per i sistemi di comando legati alla sicurezza (SRP/CS), la norma definisce cinque livelli di prestazione, da PLa a PLe. Il livello richiesto (PLr) discende dalla valutazione dei rischi; il livello raggiunto si calcola combinando:",
          list: [
            "L'architettura del sistema (categorie B, 1, 2, 3, 4)",
            "L'affidabilità dei componenti (MTTFd, ricavabile dal B10d per i componenti usurabili)",
            "La copertura diagnostica (DC): quanto il sistema rileva i propri guasti",
            "I guasti a causa comune (CCF), da valutare con una checklist dedicata",
          ],
        },
        {
          heading: "IEC 62061: i livelli SIL",
          paragraph:
            "Per i sistemi elettrici, elettronici ed elettronici programmabili (SRECS), la IEC 62061 utilizza i Safety Integrity Level (SIL 1-3). Il calcolo si basa sulla probabilità di guasto pericoloso all'ora (PFHd), sulla frazione di guasto sicuro (SFF) e sulla copertura diagnostica. Le due norme sono equivalenti per obiettivi: esiste una tabella di corrispondenza tra PL e SIL.",
        },
        {
          heading: "I dati contano",
          paragraph:
            "Il calcolo di PL o SIL richiede dati affidabili sui componenti: valori B10d e MTTFd dichiarati dai costruttori, architetture documentate, numero di operazioni annue. Nel nostro libro sulla Direttiva Macchine trovate esempi applicativi completi, con i calcoli svolti passo per passo per entrambe le norme.",
        },
        {
          heading: "Gli errori più comuni",
          list: [
            "PL dichiarato a catalogo senza il calcolo sull'architettura reale",
            "Guasti a causa comune (CCF) non valutati",
            "Funzioni di sicurezza non validate dopo l'installazione",
            "Documentazione del calcolo assente dal fascicolo tecnico",
            "Circuiti ereditati da macchine precedenti senza verifica del PLr richiesto",
          ],
        },
        {
          heading: "Con il Regolamento (UE) 2023/1230",
          paragraph:
            "Le funzioni di sicurezza restano centrali, ma il Regolamento aggiunge un tassello: i sistemi di comando devono essere protetti anche da manipolazioni intenzionali e la documentazione può essere digitale. Affidabilità e cybersecurity, come spieghiamo nell'approfondimento dedicato, oggi viaggiano insieme.",
        },
      ],
      cta: { label: "Verifica le tue funzioni di sicurezza", to: "/risk-analysis" },
    },
    en: {
      overline: "Insights · Safety",
      title: "Functional safety: PL and SIL explained to manufacturers",
      lead: "Emergency stops, interlocked movable guards, light curtains: safety functions must be reliable. EN ISO 13849-1 (PL) and IEC 62061 (SIL) state exactly how reliable — and how to prove it.",
      sections: [
        {
          heading: "What functional safety is",
          paragraph:
            "It is the part of machine safety that depends on the correct functioning of the control system: when the operator opens a guard or presses the emergency stop, the safety function must intervene with a reliability level appropriate to the risk. It is not enough that it «works»: it must work even when something fails.",
        },
        {
          heading: "EN ISO 13849-1: Performance Levels",
          paragraph:
            "For safety-related parts of control systems (SRP/CS), the standard defines five performance levels, from PLa to PLe. The required level (PLr) comes from the risk assessment; the achieved level is calculated by combining:",
          list: [
            "The system architecture (categories B, 1, 2, 3, 4)",
            "Component reliability (MTTFd, derived from B10d for wear-prone components)",
            "Diagnostic coverage (DC): how well the system detects its own faults",
            "Common cause failures (CCF), assessed with a dedicated checklist",
          ],
        },
        {
          heading: "IEC 62061: SIL levels",
          paragraph:
            "For electrical, electronic and programmable electronic systems (SRECS), IEC 62061 uses Safety Integrity Levels (SIL 1-3). The calculation is based on the probability of dangerous failure per hour (PFHd), the safe failure fraction (SFF) and diagnostic coverage. The two standards are equivalent in their objectives: a PL-to-SIL correspondence table exists.",
        },
        {
          heading: "Data matters",
          paragraph:
            "Calculating PL or SIL requires reliable component data: B10d and MTTFd values declared by manufacturers, documented architectures, annual operation counts. Our book on the Machinery Directive includes complete worked examples, with step-by-step calculations for both standards.",
        },
        {
          heading: "The most common mistakes",
          list: [
            "PL declared from catalogues without calculation on the real architecture",
            "Common cause failures (CCF) not assessed",
            "Safety functions not validated after installation",
            "Calculation documentation missing from the technical file",
            "Circuits inherited from previous machines without verifying the required PLr",
          ],
        },
        {
          heading: "With Regulation (EU) 2023/1230",
          paragraph:
            "Safety functions remain central, but the Regulation adds a piece: control systems must also be protected from intentional manipulation, and documentation may be digital. Reliability and cybersecurity, as we explain in the dedicated insight, now go hand in hand.",
        },
      ],
      cta: { label: "Verify your safety functions", to: "/risk-analysis" },
    },
  },
  "ripari-e-interblocchi": {
    heroImage: "/articles/ripari-e-interblocchi.png",
    it: {
      overline: "Approfondimenti · Sicurezza",
      title: "Ripari, interblocchi e arresto di emergenza: la protezione fisica della macchina",
      lead: "Quando la progettazione intrinsecamente sicura non basta, entrano in campo ripari e dispositivi di protezione: il secondo gradino della riduzione del rischio, regolato da norme precise e da errori che si pagano cari.",
      sections: [
        {
          heading: "Proteggere senza ostacolare",
          paragraph:
            "Ripari e dispositivi di protezione intervengono quando il pericolo non può essere eliminato alla fonte. Il compito del progettista è duplice: impedire l'accesso alle zone pericolose senza rendere la macchina inutilizzabile, e garantire che la protezione resti efficace per tutta la vita della macchina, manutenzione inclusa.",
        },
        {
          heading: "Ripari fissi e mobili",
          paragraph:
            "La normativa distingue in modo netto le due famiglie, e la scelta non è libera:",
          list: [
            "Ripari fissi: apribili solo con attrezzi, tenuti in posizione da sistemi di fissaggio che non si stacchino al loro aprirsi",
            "Ripari mobili: collegati alla zona pericolosa e associati a un dispositivo di interblocco — l'apertura comanda l'arresto",
            "Ripari regolabili: ammessi solo dove l'intervento dell'operatore è frequente e controllato",
          ],
        },
        {
          heading: "Gli interblocchi: il cuore del sistema",
          paragraph:
            "Un riparo mobile è efficace solo se il suo interblocco è affidabile: la funzione di interblocco fa parte dei circuiti di comando legati alla sicurezza e deve raggiungere il PLr o SIL richiesto dalla valutazione dei rischi. Quando la macchina ha tempi di esaurimento del moto pericolosi (rundown), serve l'interblocco con blocco del riparo: il riparo resta chiuso finché il pericolo non è cessato.",
        },
        {
          heading: "L'arresto di emergenza",
          paragraph:
            "L'arresto di emergenza è un complemento alle misure di protezione, non una misura primaria né una scorciatoia per non mettere i ripari. Le categorie di arresto sono definite dalla CEI EN 60204-1: categoria 0 (arresto per rimozione immediata dell'alimentazione), categoria 1 (arresto controllato con alimentazione mantenuta fino all'arresto) e categoria 2 (arresto controllato con alimentazione mantenuta). Il comando — il fungo rosso su fondo giallo — deve essere ben visibile, accessibile e attivo in ogni modo operativo della macchina.",
        },
        {
          heading: "Dispositivi sensibili e distanze di sicurezza",
          paragraph:
            "Barriere fotoelettriche, tappeti sensibili e scanner laser proteggono senza barriere fisiche, ma impongono un calcolo preciso: la distanza di sicurezza dalla zona pericolosa dipende dalla velocità di avvicinamento del corpo e dal tempo di arresto della macchina (EN ISO 13855). Nel nostro libro sulla Direttiva Macchine trovate esempi applicativi completi, dal calcolo delle distanze ai dispositivi ad attuazione meccanica.",
        },
        {
          heading: "Gli errori più comuni",
          list: [
            "Interblocchi facilmente bypassabili con un magnete o un ponte",
            "Distanza di sicurezza delle barriere non calcolata sul tempo di arresto reale",
            "Arresto di emergenza usato come arresto normale di ciclo",
            "Ripari rimossi per la manutenzione senza procedure e ripristino verificato",
            "Funzione di interblocco non validata dopo l'installazione o dopo modifiche",
          ],
        },
      ],
      cta: { label: "Valuta ripari e protezioni delle tue macchine", to: "/risk-analysis" },
    },
    en: {
      overline: "Insights · Safety",
      title: "Guards, interlocks and emergency stop: the machine's physical protection",
      lead: "When inherently safe design is not enough, guards and protective devices come into play: the second step of risk reduction, governed by precise standards and by mistakes that cost dearly.",
      sections: [
        {
          heading: "Protecting without obstructing",
          paragraph:
            "Guards and protective devices intervene when the hazard cannot be eliminated at the source. The designer's task is twofold: prevent access to danger zones without making the machine unusable, and ensure the protection remains effective throughout the machine's life, maintenance included.",
        },
        {
          heading: "Fixed and movable guards",
          paragraph:
            "The legislation clearly distinguishes the two families, and the choice is not free:",
          list: [
            "Fixed guards: openable only with tools, held in place by fastenings that do not detach when opened",
            "Movable guards: linked to the danger zone and associated with an interlocking device — opening commands the stop",
            "Adjustable guards: permitted only where operator intervention is frequent and controlled",
          ],
        },
        {
          heading: "Interlocks: the heart of the system",
          paragraph:
            "A movable guard is effective only if its interlock is reliable: the interlocking function is part of the safety-related control circuits and must reach the PLr or SIL required by the risk assessment. When the machine has dangerous run-down times, an interlock with guard locking is needed: the guard stays closed until the hazard has ceased.",
        },
        {
          heading: "The emergency stop",
          paragraph:
            "The emergency stop is a complement to protective measures, not a primary measure nor a shortcut to avoid guards. Stop categories are defined by IEC/EN 60204-1: category 0 (stop by immediate removal of power), category 1 (controlled stop with power maintained until standstill) and category 2 (controlled stop with power maintained). The control — the red mushroom on a yellow background — must be clearly visible, accessible and active in every operating mode of the machine.",
        },
        {
          heading: "Sensitive devices and safety distances",
          paragraph:
            "Light curtains, sensitive mats and laser scanners protect without physical barriers, but require a precise calculation: the safety distance from the danger zone depends on the body's approach speed and the machine's stopping time (EN ISO 13855). Our book on the Machinery Directive includes complete worked examples, from distance calculations to mechanically actuated devices.",
        },
        {
          heading: "The most common mistakes",
          list: [
            "Interlocks easily bypassed with a magnet or a jumper",
            "Light curtain safety distance not calculated on the real stopping time",
            "Emergency stop used as a normal cycle stop",
            "Guards removed for maintenance without procedures and verified reinstatement",
            "Interlock function not validated after installation or modifications",
          ],
        },
      ],
      cta: { label: "Assess the guards and protections of your machines", to: "/risk-analysis" },
    },
  },
  "atex": {
    heroImage: "/articles/atex.png",
    it: {
      overline: "Approfondimenti · Direttive",
      title: "Direttiva ATEX: macchine e impianti in atmosfere esplosive",
      lead: "Polveri, gas, vapori infiammabili: dove l'atmosfera può esplodere, la macchina deve essere pensata per non innescarla. La Direttiva ATEX 2014/34/UE definisce come — e si affianca alla normativa macchine.",
      sections: [
        {
          heading: "Quando si applica",
          paragraph:
            "La Direttiva ATEX riguarda apparecchi, componenti e sistemi di protezione destinati a funzionare in atmosfere potenzialmente esplosive: silos e molini per polveri, impianti di verniciatura, ambienti con gas o vapori infiammabili. Se la vostra macchina opera — anche solo occasionalmente — in uno di questi contesti, la conformità ATEX non è un optional.",
        },
        {
          heading: "Zone e categorie",
          paragraph:
            "Il datore di lavoro classifica le aree in zone; il costruttore progetta l'apparecchiatura per la categoria corrispondente:",
          list: [
            "Zone 0 e 20 (rischio continuo) → categoria 1, protezione altissima",
            "Zone 1 e 21 (rischio probabile) → categoria 2, protezione elevata",
            "Zone 2 e 22 (rischio occasionale) → categoria 3, protezione normale",
            "La lettera G indica atmosfere con gas e vapori; la D indica polveri combustibili",
          ],
        },
        {
          heading: "Il marchio Ex",
          paragraph:
            "Oltre al CE, le apparecchiature ATEX portano il marchio specifico Ex (l'esagono con la sigla εx) e una marcatura completa: gruppo, categoria, tipo di atmosfera (G/D), classe di temperatura e, per le categorie superiori, il numero dell'organismo notificato. Una marcatura incompleta è di per sé una non conformità.",
        },
        {
          heading: "Cosa deve fare il costruttore",
          list: [
            "Valutazione del rischio di esplosione della macchina, integrata nell'analisi dei rischi",
            "Eliminazione o controllo delle sorgenti di innesco efficaci: superfici calde, scintille, elettricità statica",
            "Documentazione tecnica dedicata, che include la valutazione del rischio di esplosione",
            "Dichiarazione UE di conformità che cita sia la ATEX sia la normativa macchine",
            "Istruzioni specifiche per installazione, uso e manutenzione in zona Ex",
          ],
        },
        {
          heading: "ATEX e macchine insieme",
          paragraph:
            "Le due normative si applicano contemporaneamente: la macchina segue i requisiti essenziali della normativa macchine e, per il rischio esplosione, quelli ATEX. La marcatura CE è unica, ma la dichiarazione deve citare entrambe. Con il Regolamento (UE) 2023/1230 questo impianto resta, con il riferimento aggiornato al nuovo testo.",
        },
        {
          heading: "Gli errori più comuni",
          list: [
            "Macchina venduta «normale» per una zona classificata, con adattamento lasciato all'utilizzatore",
            "Categoria dichiarata non coerente con la zona di destinazione",
            "Sorgenti di innesco non valutate nelle condizioni di guasto",
            "Marcatura Ex incompleta o mancante di classe di temperatura",
            "Manuale senza le istruzioni specifiche per l'uso in atmosfera esplosiva",
          ],
        },
      ],
      cta: { label: "Verifica la conformità ATEX", to: "/servizi/certificazioni-e-sicurezza" },
    },
    en: {
      overline: "Insights · Directives",
      title: "ATEX Directive: machines and plants in explosive atmospheres",
      lead: "Dusts, gases, flammable vapours: where the atmosphere can explode, the machine must be designed not to ignite it. The ATEX Directive 2014/34/EU defines how — and works alongside machinery legislation.",
      sections: [
        {
          heading: "When it applies",
          paragraph:
            "The ATEX Directive covers equipment, components and protective systems intended to operate in potentially explosive atmospheres: silos and mills for dusts, painting plants, environments with flammable gases or vapours. If your machine operates — even occasionally — in one of these contexts, ATEX compliance is not optional.",
        },
        {
          heading: "Zones and categories",
          paragraph:
            "The employer classifies areas into zones; the manufacturer designs the equipment for the corresponding category:",
          list: [
            "Zones 0 and 20 (continuous risk) → category 1, very high protection",
            "Zones 1 and 21 (probable risk) → category 2, high protection",
            "Zones 2 and 22 (occasional risk) → category 3, normal protection",
            "The letter G indicates gas and vapour atmospheres; D indicates combustible dusts",
          ],
        },
        {
          heading: "The Ex mark",
          paragraph:
            "In addition to the CE, ATEX equipment carries the specific Ex mark (the hexagon with the εx symbol) and a complete marking: group, category, atmosphere type (G/D), temperature class and, for the higher categories, the notified body number. An incomplete marking is itself a non-conformity.",
        },
        {
          heading: "What the manufacturer must do",
          list: [
            "Explosion risk assessment of the machine, integrated into the risk analysis",
            "Elimination or control of effective ignition sources: hot surfaces, sparks, static electricity",
            "Dedicated technical documentation, including the explosion risk assessment",
            "EU Declaration of Conformity citing both ATEX and machinery legislation",
            "Specific instructions for installation, use and maintenance in Ex zones",
          ],
        },
        {
          heading: "ATEX and machinery together",
          paragraph:
            "The two frameworks apply simultaneously: the machine follows the essential requirements of machinery legislation and, for explosion risk, those of ATEX. The CE marking is single, but the declaration must cite both. With Regulation (EU) 2023/1230 this structure remains, with the reference updated to the new text.",
        },
        {
          heading: "The most common mistakes",
          list: [
            "Machine sold as «standard» for a classified zone, with adaptation left to the user",
            "Declared category inconsistent with the destination zone",
            "Ignition sources not assessed under fault conditions",
            "Ex marking incomplete or missing the temperature class",
            "Manual without specific instructions for use in explosive atmospheres",
          ],
        },
      ],
      cta: { label: "Check your ATEX compliance", to: "/servizi/certificazioni-e-sicurezza" },
    },
  },
  "rumore": {
    heroImage: "/articles/rumore.png",
    it: {
      overline: "Approfondimenti · Sicurezza",
      title: "Il rumore delle macchine: misura, dichiarazione e riduzione",
      lead: "Il rumore è un requisito di conformità a tutti gli effetti: la normativa macchine obbliga a progettare macchine silenziose e a dichiararne le emissioni sonore con valori misurati, non stimati. Ecco come si fa sul serio.",
      sections: [
        {
          heading: "Perché il rumore è un requisito di conformità",
          paragraph:
            "Il rumore è tra i rischi fisici più sottovalutati: danneggia l'udito in modo progressivo e irreversibile. Per questo la normativa macchine richiede di progettare e costruire la macchina in modo da ridurre le emissioni sonore al minimo, tenendo conto dei progressi tecnici — e di dichiararne i valori nel manuale.",
        },
        {
          heading: "Cosa va dichiarato",
          paragraph:
            "La dichiarazione delle emissioni sonore, riportata nelle istruzioni, comprende:",
          list: [
            "Il livello di pressione acustica ponderato A al posto di lavoro (LpA), se superiore a 70 dB(A)",
            "Il livello di potenza sonora (LwA), quando la pressione supera 80 dB(A)",
            "Il valore di picco ponderato C (LpCpeak) al posto di lavoro, quando rilevante",
            "L'incertezza di misura associata ai valori dichiarati",
            "Le condizioni operative della macchina durante la misura",
          ],
        },
        {
          heading: "Come si misura",
          paragraph:
            "Le misure vanno eseguite secondo le norme specifiche (serie EN ISO 3740 e norme di prova rumore di tipo C per le singole famiglie di macchine), in condizioni operative riproducibili e documentate: carico, velocità, ciclo di lavoro. Una misura fatta «a orecchio» o in condizioni non rappresentative non ha valore di conformità.",
        },
        {
          heading: "Ridurre alla fonte",
          paragraph:
            "La via corretta è la progettazione a bassa rumorosità: scelta di componenti silenziosi, isolamento delle sorgenti, carterature insonorizzanti, giunzioni antivibranti. Le protezioni aggiuntive (cabine, barriere) vengono dopo — e le protezioni individuali, gli otoprotettori, restano l'ultima linea, a carico dell'utilizzatore.",
        },
        {
          heading: "Gli errori più comuni",
          list: [
            "Valori dichiarati copiati da schede commerciali senza misura reale",
            "Misure eseguite a macchina scarica, non nel ciclo di lavoro reale",
            "Incertezza di misura non riportata",
            "Nessuna indicazione nel manuale sulle condizioni di prova",
            "Rumore affrontato solo con otoprotettori invece che in progettazione",
          ],
        },
      ],
      cta: { label: "Prenota le prove di rumore", to: "/risk-analysis" },
    },
    en: {
      overline: "Insights · Safety",
      title: "Machinery noise: measurement, declaration and reduction",
      lead: "Noise is a conformity requirement in every respect: machinery legislation requires machines to be designed quiet and their noise emissions declared with measured, not estimated, values. Here is how to do it properly.",
      sections: [
        {
          heading: "Why noise is a conformity requirement",
          paragraph:
            "Noise is among the most underestimated physical risks: it damages hearing progressively and irreversibly. This is why machinery legislation requires machines to be designed and built to reduce noise emissions to a minimum, taking technical progress into account — and to declare the values in the manual.",
        },
        {
          heading: "What must be declared",
          paragraph:
            "The noise emission declaration, reported in the instructions, includes:",
          list: [
            "The A-weighted emission sound pressure level at the workstation (LpA), if above 70 dB(A)",
            "The sound power level (LwA), when the pressure level exceeds 80 dB(A)",
            "The C-weighted peak value (LpCpeak) at the workstation, where relevant",
            "The measurement uncertainty associated with the declared values",
            "The machine operating conditions during measurement",
          ],
        },
        {
          heading: "How to measure",
          paragraph:
            "Measurements must be performed according to the specific standards (EN ISO 3740 series and type-C noise test codes for individual machine families), under reproducible and documented operating conditions: load, speed, work cycle. A measurement done «by ear» or in non-representative conditions has no conformity value.",
        },
        {
          heading: "Reducing at the source",
          paragraph:
            "The correct path is low-noise design: quiet component selection, source isolation, sound-insulating enclosures, anti-vibration couplings. Additional protections (booths, barriers) come after — and personal protective equipment, ear defenders, remains the last line, at the user's expense.",
        },
        {
          heading: "The most common mistakes",
          list: [
            "Declared values copied from commercial datasheets without real measurement",
            "Measurements performed with the machine unloaded, not in the real work cycle",
            "Measurement uncertainty not reported",
            "No indication in the manual about test conditions",
            "Noise addressed only with ear defenders instead of in the design",
          ],
        },
      ],
      cta: { label: "Book noise testing", to: "/risk-analysis" },
    },
  },
  "ped": {
    heroImage: "/articles/ped.png",
    it: {
      overline: "Approfondimenti · Direttive",
      title: "La Direttiva PED applicata alle macchine: attrezzature a pressione senza sorprese",
      lead: "Serbatoi, tubazioni, scambiatori, caldaie: molte macchine incorporano attrezzature a pressione, e con esse entra in gioco la Direttiva PED 2014/68/UE. Capire quando e come si applica evita fermi e responsabilità.",
      sections: [
        {
          heading: "Quando una macchina «diventa» PED",
          paragraph:
            "La PED si applica alle attrezzature a pressione — recipienti, tubazioni, accessori, parti in pressione — con pressione massima ammissibile PS superiore a 0,5 bar. Se la vostra macchina incorpora un serbatoio in pressione, un circuito idraulico dedicato o una tubazione di processo, molto probabilmente una parte del prodotto rientra nel campo PED, con obblighi propri e separati.",
        },
        {
          heading: "Classificazione e categorie",
          paragraph:
            "La PED classifica le attrezzature in categorie di rischio (da I a IV) in base a pressione, volume o diametro, e tipo di fluido — gas pericolosi, liquidi infiammabili, vapore. Più alta la categoria, più stringente la valutazione di conformità: dal controllo interno del fabbricante fino al coinvolgimento obbligatorio di un organismo notificato.",
        },
        {
          heading: "Moduli e organismi notificati",
          paragraph:
            "La valutazione di conformità segue i moduli previsti dalla direttiva: per la categoria I il fabbricante può autocertificare con controllo di fabbricazione interno; dalla categoria II in su entra in gioco un organismo notificato, che esamina la documentazione, assiste alle prove (come la prova idraulica) e appone il proprio numero accanto al marchio CE.",
        },
        {
          heading: "PED e normativa macchine insieme",
          paragraph:
            "Per i pericoli da pressione delle attrezzature incorporate si applica la PED; per tutto il resto, la normativa macchine. Le due conformità convivono nello stesso prodotto: la marcatura CE è unica, la Dichiarazione di conformità cita entrambi i riferimenti, e il fascicolo tecnico deve coprire sia la macchina sia l'attrezzatura a pressione.",
        },
        {
          heading: "Documentazione e marcatura",
          list: [
            "Documentazione tecnica: disegni, calcoli di resistenza, qualifiche di saldatura, certificati dei materiali",
            "Prove di verifica finale, inclusa la prova di pressione dove prevista",
            "Marcatura con i dati essenziali: fabbricante, anno, pressione, volume, numero di serie",
            "Dichiarazione UE di conformità e istruzioni nella lingua di destinazione",
          ],
        },
        {
          heading: "Gli errori più comuni",
          list: [
            "Attrezzatura a pressione incorporata nella macchina senza valutare la PED",
            "Classificazione di categoria errata per fluido o volume",
            "Organismo notificato non coinvolto dove obbligatorio",
            "Documentazione di saldatura e materiali incompleta",
            "Dichiarazione di conformità che cita solo la normativa macchine",
          ],
        },
      ],
      cta: { label: "Verifica la conformità PED", to: "/servizi/certificazioni-e-sicurezza" },
    },
    en: {
      overline: "Insights · Directives",
      title: "The PED Directive applied to machines: pressure equipment without surprises",
      lead: "Vessels, piping, heat exchangers, boilers: many machines incorporate pressure equipment, and with it the PED Directive 2014/68/EU comes into play. Understanding when and how it applies prevents stoppages and liability.",
      sections: [
        {
          heading: "When a machine «becomes» PED",
          paragraph:
            "The PED applies to pressure equipment — vessels, piping, accessories, pressurised parts — with a maximum allowable pressure PS above 0.5 bar. If your machine incorporates a pressure vessel, a dedicated hydraulic circuit or process piping, part of the product very likely falls within the PED scope, with its own separate obligations.",
        },
        {
          heading: "Classification and categories",
          paragraph:
            "The PED classifies equipment into risk categories (I to IV) based on pressure, volume or diameter, and fluid type — hazardous gases, flammable liquids, steam. The higher the category, the stricter the conformity assessment: from the manufacturer's internal control up to the mandatory involvement of a notified body.",
        },
        {
          heading: "Modules and notified bodies",
          paragraph:
            "Conformity assessment follows the modules set out by the directive: for category I the manufacturer may self-certify with internal manufacturing control; from category II upwards a notified body comes into play, examining the documentation, witnessing tests (such as the hydrostatic test) and affixing its number next to the CE mark.",
        },
        {
          heading: "PED and machinery legislation together",
          paragraph:
            "For pressure hazards of incorporated equipment, the PED applies; for everything else, machinery legislation. The two conformities coexist in the same product: the CE marking is single, the Declaration of Conformity cites both references, and the technical file must cover both the machine and the pressure equipment.",
        },
        {
          heading: "Documentation and marking",
          list: [
            "Technical documentation: drawings, strength calculations, welding qualifications, material certificates",
            "Final verification tests, including the pressure test where required",
            "Marking with essential data: manufacturer, year, pressure, volume, serial number",
            "EU Declaration of Conformity and instructions in the destination language",
          ],
        },
        {
          heading: "The most common mistakes",
          list: [
            "Pressure equipment incorporated into the machine without assessing the PED",
            "Wrong category classification for fluid or volume",
            "Notified body not involved where mandatory",
            "Incomplete welding and material documentation",
            "Declaration of Conformity citing only machinery legislation",
          ],
        },
      ],
      cta: { label: "Check your PED compliance", to: "/servizi/certificazioni-e-sicurezza" },
    },
  },
  "presse": {
    heroImage: "/articles/presse.png",
    it: {
      overline: "Approfondimenti · Sicurezza",
      title: "Sicurezza delle presse: dove l'esperienza conta di più",
      lead: "Le presse sono storicamente tra le macchine più pericolose dell'industria: elevate forze, tempi di ciclo rapidi e interazione continua con l'operatore. La loro sicurezza si gioca su norme specifiche e su scelte che non ammettono approssimazione.",
      sections: [
        {
          heading: "Un rischio che non ammette errori",
          paragraph:
            "Punzonatura, tranciatura, stampaggio: nella pressa la zona di lavoro è accessibile e le forze in gioco sono enormi. Le statistiche infortunistiche lo confermano da decenni: gli infortuni alle mani sulle presse sono tra i più gravi del settore meccanico. Per questo le presse dispongono di norme armonizzate di tipo C dedicate, che specificano i requisiti in modo cogente.",
        },
        {
          heading: "Le norme di riferimento",
          paragraph:
            "Le presse meccaniche seguono la EN 692, le presse idrauliche la EN 693. Le norme definiscono nel dettaglio le misure di sicurezza: ripari fissi e interbloccati, comandi bimanuali, barriere fotoelettriche (ESPE) di tipo 4, caratteristiche dei circuiti idraulici ed elettrici di sicurezza, controlli degli sbilanciamenti e protezione contro gli interventi non intenzionali dell'attrezzo.",
        },
        {
          heading: "Le misure di protezione tipiche",
          list: [
            "Comandi bimanuali con contemporaneità e riarmo obbligatorio tra un ciclo e l'altro",
            "Barriere fotoelettriche con distanza di sicurezza calcolata sul tempo di arresto",
            "Ripari interbloccati per le zone accessibili della pressa e del volano",
            "Arresto di emergenza e funzioni di sicurezza con PL/SIL adeguato alla EN 13849-1 o IEC 62061",
            "Protezione contro la caduta dell'attrezzo e gli interventi inattesi della slitta",
          ],
        },
        {
          heading: "Retrofit e modifiche: attenzione alla conformità",
          paragraph:
            "Molte presse in servizio sono macchine mature sottoposte a modifiche: nuovi controlli, cambio dei comandi, automazione dell'alimentazione. Una modifica sostanziale può richiedere una nuova valutazione di conformità: aggiornare la sicurezza senza aggiornare la documentazione è tra gli errori più frequenti e più rischiosi.",
        },
        {
          heading: "Gli errori più comuni",
          list: [
            "Comandi bimanuali installati senza verifica di contemporaneità e distanza",
            "Barriere fotoelettriche posizionate senza il calcolo della distanza di sicurezza",
            "Interblocchi dei ripari bypassabili o non monitorati",
            "Presse modificate senza aggiornare valutazione dei rischi e fascicolo tecnico",
            "Manutenzione eseguita senza procedure di isolamento delle energie",
          ],
        },
      ],
      cta: { label: "Metti in sicurezza le tue presse", to: "/risk-analysis" },
    },
    en: {
      overline: "Insights · Safety",
      title: "Press safety: where experience matters most",
      lead: "Presses are historically among the most dangerous machines in industry: huge forces, fast cycle times and continuous interaction with the operator. Their safety rests on specific standards and choices that tolerate no approximation.",
      sections: [
        {
          heading: "A risk that admits no mistakes",
          paragraph:
            "Punching, shearing, stamping: in a press the working zone is accessible and the forces involved are enormous. Accident statistics have confirmed this for decades: hand injuries on presses are among the most severe in the mechanical sector. This is why presses have dedicated harmonised type-C standards that specify requirements mandatorily.",
        },
        {
          heading: "The reference standards",
          paragraph:
            "Mechanical presses follow EN 692, hydraulic presses EN 693. The standards define safety measures in detail: fixed and interlocked guards, two-hand controls, type 4 light curtains (ESPE), characteristics of hydraulic and electrical safety circuits, imbalance checks and protection against unintended tool actuation.",
        },
        {
          heading: "The typical protective measures",
          list: [
            "Two-hand controls with simultaneity and mandatory re-arm between cycles",
            "Light curtains with safety distance calculated on the stopping time",
            "Interlocked guards for accessible areas of the press and flywheel",
            "Emergency stop and safety functions with adequate PL/SIL per EN 13849-1 or IEC 62061",
            "Protection against tool drop and unintended ram movements",
          ],
        },
        {
          heading: "Retrofits and modifications: mind the conformity",
          paragraph:
            "Many presses in service are mature machines that have undergone modifications: new controls, changed commands, automated feeding. A substantial modification may require a new conformity assessment: upgrading safety without upgrading documentation is among the most frequent and riskiest mistakes.",
        },
        {
          heading: "The most common mistakes",
          list: [
            "Two-hand controls installed without verifying simultaneity and distance",
            "Light curtains positioned without the safety distance calculation",
            "Guard interlocks bypassable or not monitored",
            "Presses modified without updating the risk assessment and technical file",
            "Maintenance performed without energy isolation procedures",
          ],
        },
      ],
      cta: { label: "Make your presses safe", to: "/risk-analysis" },
    },
  },
  "emc": {
    heroImage: "/articles/emc.png",
    it: {
      overline: "Approfondimenti · Direttive",
      title: "Compatibilità elettromagnetica (EMC): macchine immuni e rispettose",
      lead: "Una macchina deve funzionare correttamente nel suo ambiente elettromagnetico senza disturbare le altre: è la Direttiva EMC 2014/30/UE. Per le macchine moderne, piene di elettronica, non è una formalità.",
      sections: [
        {
          heading: "Due facce della stessa moneta",
          paragraph:
            "La compatibilità elettromagnetica ha due versanti: l'emissione — la macchina non deve generare disturbi che compromettano altre apparecchiature — e l'immunità — la macchina deve continuare a funzionare in sicurezza anche in presenza di disturbi esterni. Per le macchine, la Direttiva EMC si affianca alla normativa macchine e ne completa i requisiti elettrici.",
        },
        {
          heading: "Le norme per l'industria",
          paragraph:
            "Per gli ambienti industriali le norme di riferimento sono la serie EN 61000-6-2 (immunità) e EN 61000-6-4 (emissione), affiancate dalle norme di prodotto per azionamenti e regolatori (EN 61800-3) e dai requisiti EMC della CEI EN 60204-1 per l'equipaggiamento elettrico delle macchine.",
        },
        {
          heading: "Cosa va verificato e documentato",
          list: [
            "Livelli di emissione condotta e irradiata nei limiti della classe prevista",
            "Immunità a scariche elettrostatiche, campi irradiati, burst, surge e variazioni di rete",
            "Comportamento sicuro delle funzioni di sicurezza anche in presenza di disturbi",
            "Documentazione tecnica con valutazione EMC, norme applicate e risultati delle prove",
            "Istruzioni di installazione: schermatura dei cavi, messa a terra, filtri, separazione dei percorsi",
          ],
        },
        {
          heading: "EMC e sicurezza funzionale",
          paragraph:
            "Un disturbo elettromagnetico che inibisce una barriera fotoelettrica o falsifica un comando è un problema di sicurezza, non solo di qualità. Per questo le norme di sicurezza funzionale richiedono livelli di immunità superiori per le parti dei sistemi di comando legate alla sicurezza: EMC e PL/SIL vanno progettati insieme.",
        },
        {
          heading: "Gli errori più comuni",
          list: [
            "Prove EMC eseguite sulla macchina isolata e non nella configurazione di installazione reale",
            "Filtri e schermi previsti a progetto ma non installati in campo",
            "Cavi di potenza e di segnale instradati insieme",
            "Documentazione EMC assente dal fascicolo tecnico",
            "Nessuna verifica EMC dopo sostituzione di azionamenti o PLC",
          ],
        },
      ],
      cta: { label: "Pianifica le verifiche EMC", to: "/servizi/certificazioni-e-sicurezza" },
    },
    en: {
      overline: "Insights · Directives",
      title: "Electromagnetic compatibility (EMC): immune and respectful machines",
      lead: "A machine must operate correctly in its electromagnetic environment without disturbing others: that is the EMC Directive 2014/30/EU. For modern machines, full of electronics, it is no formality.",
      sections: [
        {
          heading: "Two sides of the same coin",
          paragraph:
            "Electromagnetic compatibility has two sides: emission — the machine must not generate disturbances that compromise other equipment — and immunity — the machine must continue to operate safely even in the presence of external disturbances. For machines, the EMC Directive works alongside machinery legislation and completes its electrical requirements.",
        },
        {
          heading: "The standards for industry",
          paragraph:
            "For industrial environments the reference standards are the EN 61000-6-2 (immunity) and EN 61000-6-4 (emission) series, flanked by product standards for drives and regulators (EN 61800-3) and by the EMC requirements of IEC/EN 60204-1 for the electrical equipment of machines.",
        },
        {
          heading: "What must be verified and documented",
          list: [
            "Conducted and radiated emission levels within the limits of the intended class",
            "Immunity to electrostatic discharges, radiated fields, bursts, surges and supply variations",
            "Safe behaviour of safety functions even in the presence of disturbances",
            "Technical documentation with EMC assessment, applied standards and test results",
            "Installation instructions: cable shielding, earthing, filters, segregation of cable routes",
          ],
        },
        {
          heading: "EMC and functional safety",
          paragraph:
            "An electromagnetic disturbance that inhibits a light curtain or falsifies a command is a safety problem, not just a quality one. This is why functional safety standards require higher immunity levels for the safety-related parts of control systems: EMC and PL/SIL must be designed together.",
        },
        {
          heading: "The most common mistakes",
          list: [
            "EMC tests performed on the isolated machine, not in the real installation configuration",
            "Filters and shields planned in design but not installed in the field",
            "Power and signal cables routed together",
            "EMC documentation missing from the technical file",
            "No EMC verification after replacing drives or PLCs",
          ],
        },
      ],
      cta: { label: "Plan your EMC verifications", to: "/servizi/certificazioni-e-sicurezza" },
    },
  },
  "dispositivi-medici": {
    heroImage: "/articles/dispositivi-medici.png",
    it: {
      overline: "Approfondimenti · Direttive",
      title: "Dispositivi medici e macchine: quando le normative si incontrano",
      lead: "Sterilizzatrici, macchine diagnostiche, apparecchiature di laboratorio: molti prodotti sono al tempo stesso macchine e dispositivi medici. Il Regolamento (UE) 2017/745 (MDR) ha alzato l'asticella: ecco come orientarsi.",
      sections: [
        {
          heading: "Due mondi che si sovrappongono",
          paragraph:
            "La normativa macchine copre i pericoli meccanici, elettrici e funzionali; il Regolamento sui dispositivi medici copre la sicurezza del paziente e le prestazioni cliniche. Un'autoclave da sterilizzazione o una macchina per diagnostica possono ricadere in entrambi i perimetri: i requisiti si cumulano, non si sostituiscono.",
        },
        {
          heading: "Dalla Direttiva al Regolamento MDR",
          paragraph:
            "Il Regolamento (UE) 2017/745 ha sostituito la vecchia Direttiva 93/42/CEE con requisiti più stringenti: classificazione per classi di rischio (I, IIa, IIb, III), maggior coinvolgimento degli organismi notificati, fascicolo tecnico rafforzato, sorveglianza post-immissione e tracciabilità (UDI). Chi certificava «in leggera» deve rimettere tutto in discussione.",
        },
        {
          heading: "Cosa cambia per il costruttore",
          list: [
            "Classificazione corretta del dispositivo e percorso di valutazione coerente",
            "Gestione del rischio secondo EN ISO 14971 per tutto il ciclo di vita",
            "Sicurezza elettrica e compatibilità elettromagnetica secondo la serie EN 60601",
            "Fascicolo tecnico MDR integrato con la documentazione macchina",
            "Sistema qualità e sorveglianza post-vendita documentati",
          ],
        },
        {
          heading: "Il punto di incontro: la sicurezza funzionale",
          paragraph:
            "Nei dispositivi medici motorizzati — letti operatori, sistemi di movimentazione paziente, automazioni di laboratorio — le funzioni di sicurezza seguono logiche analoghe a PL e SIL, declinate dalle norme di prodotto. Progettare la sicurezza una volta sola, coprendo entrambi i quadri normativi, è la strada più efficiente.",
        },
        {
          heading: "Gli errori più comuni",
          list: [
            "Prodotto classificato solo come macchina, ignorando la destinazione medica",
            "Gestione del rischio ISO 14971 separata e incoerente con l'analisi macchina",
            "Fascicolo tecnico non allineato ai requisiti MDR",
            "Sorveglianza post-vendita assente o solo formale",
            "Nessuna strategia per la tracciabilità UDI",
          ],
        },
      ],
      cta: { label: "Valuta il doppio percorso di conformità", to: "/servizi/certificazioni-e-sicurezza" },
    },
    en: {
      overline: "Insights · Directives",
      title: "Medical devices and machines: when regulations meet",
      lead: "Sterilizers, diagnostic machines, laboratory equipment: many products are both machines and medical devices. Regulation (EU) 2017/745 (MDR) has raised the bar: here is how to find your way.",
      sections: [
        {
          heading: "Two overlapping worlds",
          paragraph:
            "Machinery legislation covers mechanical, electrical and functional hazards; the Medical Device Regulation covers patient safety and clinical performance. A sterilization autoclave or a diagnostic machine can fall within both perimeters: the requirements add up, they do not replace each other.",
        },
        {
          heading: "From Directive to MDR",
          paragraph:
            "Regulation (EU) 2017/745 replaced the old Directive 93/42/EEC with stricter requirements: risk-based classification (classes I, IIa, IIb, III), greater involvement of notified bodies, a strengthened technical file, post-market surveillance and traceability (UDI). Those who certified «lightly» must rethink everything.",
        },
        {
          heading: "What changes for the manufacturer",
          list: [
            "Correct device classification and a consistent assessment route",
            "Risk management according to EN ISO 14971 throughout the lifecycle",
            "Electrical safety and electromagnetic compatibility per the EN 60601 series",
            "MDR technical file integrated with the machinery documentation",
            "Documented quality system and post-market surveillance",
          ],
        },
        {
          heading: "The meeting point: functional safety",
          paragraph:
            "In motorised medical devices — operating tables, patient handling systems, laboratory automation — safety functions follow logics analogous to PL and SIL, declined by the product standards. Designing safety once, covering both regulatory frameworks, is the most efficient path.",
        },
        {
          heading: "The most common mistakes",
          list: [
            "Product classified only as a machine, ignoring the medical intended purpose",
            "ISO 14971 risk management separate from and inconsistent with the machine analysis",
            "Technical file not aligned with MDR requirements",
            "Post-market surveillance absent or merely formal",
            "No strategy for UDI traceability",
          ],
        },
      ],
      cta: { label: "Assess the dual compliance route", to: "/servizi/certificazioni-e-sicurezza" },
    },
  },
  "farma": {
    heroImage: "/articles/farma.png",
    it: {
      overline: "Approfondimenti · Settori",
      title: "Sicurezza delle macchine nel settore farmaceutico",
      lead: "Nel farmaceutico la sicurezza della macchina incontra la protezione del prodotto: GMP, progettazione igienica, contenimento e validazione. Una conformità a due binari che va progettata insieme, non rincorsa.",
      sections: [
        {
          heading: "Due conformità, una macchina",
          paragraph:
            "Una linea farmaceutica deve proteggere l'operatore — come ogni macchina — e contemporaneamente proteggere il prodotto da contaminazioni. Normativa macchine e GMP (Good Manufacturing Practice) convivono nello stesso progetto: ripari e interblocchi da un lato, pulibilità e materiali di contatto dall'altro.",
        },
        {
          heading: "Progettazione igienica",
          paragraph:
            "La normativa macchine stessa lo prevede: le macchine per prodotti farmaceutici (come quelle per derrate alimentari e cosmetici) devono rispettare requisiti di igiene rafforzati. In pratica: superfici lisce e pulibili, materiali idonei al contatto, assenza di interstizi dove si accumulano residui, possibilità di pulizia e disinfezione senza smontaggio complesso.",
        },
        {
          heading: "Contenimento e protezione dell'operatore",
          paragraph:
            "Con principi attivi ad alta potenza il rischio si inverte: è l'operatore a dover essere protetto dal prodotto. Isolatori, sistemi di contenimento, estrazioni localizzate e verifiche di tenuta diventano parte integrante della valutazione dei rischi della macchina, accanto ai pericoli meccanici tradizionali.",
        },
        {
          heading: "La validazione: documentare per dimostrare",
          list: [
            "FAT (Factory Acceptance Test) e SAT (Site Acceptance Test) documentati",
            "Qualifiche IQ/OQ/PQ integrate con le verifiche di sicurezza macchina",
            "Fascicolo tecnico completo, inclusa la documentazione di sicurezza funzionale",
            "Procedure di pulizia e cambio formato che non eludano le protezioni",
            "Gestione delle modifiche: ogni retrofit rivalutato sia lato GMP sia lato sicurezza",
          ],
        },
        {
          heading: "Gli errori più comuni",
          list: [
            "Ripari progettati bene per la sicurezza ma impossibili da pulire",
            "Interblocchi bypassati durante le operazioni di cambio formato",
            "Documentazione GMP e fascicolo tecnico macchina che non si parlano",
            "Sistemi di contenimento aggiunti dopo senza rivalutare i rischi meccanici",
            "Formazione degli operatori solo sul processo, non sui pericoli della macchina",
          ],
        },
      ],
      cta: { label: "Parliamone con un nostro esperto", to: "/contatti" },
    },
    en: {
      overline: "Insights · Sectors",
      title: "Machinery safety in the pharmaceutical sector",
      lead: "In pharma, machine safety meets product protection: GMP, hygienic design, containment and validation. A two-track compliance that must be designed together, not chased afterwards.",
      sections: [
        {
          heading: "Two compliances, one machine",
          paragraph:
            "A pharmaceutical line must protect the operator — like any machine — and at the same time protect the product from contamination. Machinery legislation and GMP (Good Manufacturing Practice) coexist in the same project: guards and interlocks on one side, cleanability and contact materials on the other.",
        },
        {
          heading: "Hygienic design",
          paragraph:
            "Machinery legislation itself provides for it: machines for pharmaceutical products (like those for foodstuffs and cosmetics) must meet enhanced hygiene requirements. In practice: smooth, cleanable surfaces, contact-suitable materials, no crevices where residues accumulate, and cleaning and disinfection possible without complex disassembly.",
        },
        {
          heading: "Containment and operator protection",
          paragraph:
            "With high-potency active ingredients the risk is reversed: it is the operator who must be protected from the product. Isolators, containment systems, localized extraction and leak-tightness verification become an integral part of the machine's risk assessment, alongside traditional mechanical hazards.",
        },
        {
          heading: "Validation: document to demonstrate",
          list: [
            "Documented FAT (Factory Acceptance Test) and SAT (Site Acceptance Test)",
            "IQ/OQ/PQ qualifications integrated with machine safety verifications",
            "Complete technical file, including functional safety documentation",
            "Cleaning and format-change procedures that do not defeat protections",
            "Change management: every retrofit reassessed on both the GMP and safety sides",
          ],
        },
        {
          heading: "The most common mistakes",
          list: [
            "Guards well designed for safety but impossible to clean",
            "Interlocks bypassed during format-change operations",
            "GMP documentation and the machine technical file not aligned",
            "Containment systems added later without reassessing mechanical risks",
            "Operator training on the process only, not on machine hazards",
          ],
        },
      ],
      cta: { label: "Talk to one of our experts", to: "/contatti" },
    },
  },
};
